import React, { useState, useEffect } from 'react';
import { RefreshCw, X, Download, Info } from 'lucide-react';
import { useHapticFeedback } from '../../hooks/useHapticFeedback';

// ============================================================================
// TIPOS
// ============================================================================

export interface UpdateNotificationProps {
  isVisible: boolean;
  newVersion?: string | null;
  isUpdating?: boolean;
  onUpdate: () => void;
  onDismiss: () => void;
  autoUpdateDelay?: number; // Tiempo en ms antes de actualizar automáticamente (0 = deshabilitado)
  position?: 'top' | 'bottom';
  showVersionInfo?: boolean;
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export const UpdateNotification: React.FC<UpdateNotificationProps> = ({
  isVisible,
  newVersion,
  isUpdating = false,
  onUpdate,
  onDismiss,
  autoUpdateDelay = 0,
  position = 'top',
  showVersionInfo = false
}) => {
  const [countdown, setCountdown] = useState<number>(0);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const haptic = useHapticFeedback();

  // ============================================================================
  // EFECTOS
  // ============================================================================

  /**
   * Manejar countdown para auto-actualización
   */
  useEffect(() => {
    if (!isVisible || autoUpdateDelay === 0 || isUpdating) {
      setIsCountingDown(false);
      setCountdown(0);
      return;
    }

    // Iniciar countdown
    setIsCountingDown(true);
    setCountdown(Math.floor(autoUpdateDelay / 1000));

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setIsCountingDown(false);
          onUpdate();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [isVisible, autoUpdateDelay, isUpdating, onUpdate]);

  /**
   * Feedback háptico al aparecer
   */
  useEffect(() => {
    if (isVisible) {
      haptic.successFeedback();
    }
  }, [isVisible, haptic]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleUpdate = () => {
    haptic.selectionFeedback();
    setIsCountingDown(false);
    onUpdate();
  };

  const handleDismiss = () => {
    haptic.selectionFeedback();
    setIsCountingDown(false);
    onDismiss();
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!isVisible) return null;

  const positionClasses = position === 'top' 
    ? 'top-4 animate-slide-down' 
    : 'bottom-4 animate-slide-up';

  return (
    <>
      {/* Overlay para móvil (opcional) */}
      <div 
        className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[9998] lg:hidden transition-opacity"
        onClick={handleDismiss}
      />

      {/* Banner de actualización */}
      <div 
        className={`fixed left-1/2 -translate-x-1/2 ${positionClasses} z-[9999] w-[calc(100%-2rem)] max-w-md mx-auto`}
        role="alert"
        aria-live="polite"
      >
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 text-white rounded-lg shadow-2xl border border-blue-500/30 overflow-hidden">
          {/* Barra de progreso superior (si hay countdown) */}
          {isCountingDown && countdown > 0 && (
            <div className="h-1 bg-blue-800/30 relative overflow-hidden">
              <div 
                className="absolute inset-y-0 left-0 bg-white/50 transition-all duration-1000 ease-linear"
                style={{ 
                  width: `${((autoUpdateDelay / 1000 - countdown) / (autoUpdateDelay / 1000)) * 100}%` 
                }}
              />
            </div>
          )}

          <div className="p-4">
            <div className="flex items-start gap-3">
              {/* Icono animado */}
              <div className="flex-shrink-0 mt-0.5">
                {isUpdating ? (
                  <RefreshCw className="h-5 w-5 animate-spin" />
                ) : (
                  <Download className="h-5 w-5 animate-bounce" />
                )}
              </div>

              {/* Contenido */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <h3 className="font-semibold text-base">
                      {isUpdating ? 'Actualizando...' : '¡Nueva versión disponible!'}
                    </h3>
                    {showVersionInfo && newVersion && !isUpdating && (
                      <p className="text-xs text-blue-100 mt-0.5 flex items-center gap-1">
                        <Info className="h-3 w-3" />
                        Versión: {newVersion.substring(0, 10)}
                      </p>
                    )}
                  </div>

                  {/* Botón cerrar */}
                  {!isUpdating && (
                    <button
                      onClick={handleDismiss}
                      className="flex-shrink-0 p-1 rounded-full hover:bg-white/20 transition-colors"
                      aria-label="Cerrar notificación"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {!isUpdating && (
                  <>
                    <p className="text-sm text-blue-50 mb-3">
                      {isCountingDown && countdown > 0
                        ? `Actualización automática en ${countdown}s...`
                        : 'Hay mejoras y correcciones disponibles'}
                    </p>

                    {/* Botones de acción */}
                    <div className="flex gap-2">
                      <button
                        onClick={handleUpdate}
                        className="flex-1 sm:flex-initial px-4 py-2 bg-white text-blue-700 rounded-lg font-medium text-sm hover:bg-blue-50 active:bg-blue-100 transition-colors flex items-center justify-center gap-2 shadow-lg"
                      >
                        <RefreshCw className="h-4 w-4" />
                        Actualizar ahora
                      </button>

                      {isCountingDown && (
                        <button
                          onClick={() => setIsCountingDown(false)}
                          className="px-4 py-2 bg-blue-800/50 text-white rounded-lg font-medium text-sm hover:bg-blue-800/70 active:bg-blue-800 transition-colors"
                        >
                          Cancelar
                        </button>
                      )}
                    </div>
                  </>
                )}

                {isUpdating && (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-blue-800/30 rounded-full overflow-hidden">
                      <div className="h-full bg-white/70 rounded-full animate-pulse" style={{ width: '60%' }} />
                    </div>
                    <span className="text-xs text-blue-100">60%</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Decoración inferior */}
          <div className="h-0.5 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
        </div>
      </div>
    </>
  );
};

// ============================================================================
// COMPONENTE COMPACTO (para esquinas)
// ============================================================================

export interface UpdateNotificationCompactProps {
  isVisible: boolean;
  onUpdate: () => void;
  onDismiss: () => void;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

export const UpdateNotificationCompact: React.FC<UpdateNotificationCompactProps> = ({
  isVisible,
  onUpdate,
  onDismiss,
  position = 'bottom-right'
}) => {
  const haptic = useHapticFeedback();

  const handleUpdate = () => {
    haptic.selectionFeedback();
    onUpdate();
  };

  const handleDismiss = () => {
    haptic.selectionFeedback();
    onDismiss();
  };

  if (!isVisible) return null;

  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-20 right-4 lg:bottom-4',
    'bottom-left': 'bottom-20 left-4 lg:bottom-4'
  }[position];

  return (
    <div 
      className={`fixed ${positionClasses} z-[9999] animate-slide-in-up`}
      role="alert"
      aria-live="polite"
    >
      <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-full shadow-xl border border-blue-500/30 flex items-center gap-2 px-4 py-3 hover:shadow-2xl transition-shadow">
        <Download className="h-4 w-4 animate-bounce" />
        <span className="text-sm font-medium whitespace-nowrap">Nueva versión</span>
        
        <button
          onClick={handleUpdate}
          className="ml-2 px-3 py-1 bg-white text-blue-700 rounded-full text-xs font-semibold hover:bg-blue-50 transition-colors"
        >
          Actualizar
        </button>
        
        <button
          onClick={handleDismiss}
          className="ml-1 p-1 hover:bg-white/20 rounded-full transition-colors"
          aria-label="Cerrar"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// ESTILOS CSS ADICIONALES (agregar a index.css si no existen)
// ============================================================================

/*
@keyframes slide-down {
  from {
    opacity: 0;
    transform: translate(-50%, -100%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

@keyframes slide-up {
  from {
    opacity: 0;
    transform: translate(-50%, 100%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

@keyframes slide-in-up {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-slide-down {
  animation: slide-down 0.3s ease-out;
}

.animate-slide-up {
  animation: slide-up 0.3s ease-out;
}

.animate-slide-in-up {
  animation: slide-in-up 0.3s ease-out;
}
*/

export default UpdateNotification;

